import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Link2Off, ShieldCheck } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { API, TOKEN_KEY } from "@/lib/api";

// Official multi-color Google "G" mark for a standard connect button.
export const GoogleG = ({ className }) => (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
        <path
            fill="#EA4335"
            d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
            fill="#4285F4"
            d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        />
        <path
            fill="#FBBC05"
            d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
            fill="#34A853"
            d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
    </svg>
);

export const GoogleConnectDialog = ({ open, onOpenChange }) => {
    const { user, refreshUser } = useAuth();
    const [working, setWorking] = useState(false);
    const openerRef = useRef(null);

    const connected = !!user?.google_connected;
    const accountEmail = user?.google_account_email || "";

    const handleConnect = () => {
        // Full-page redirect to the backend OAuth start. The app JWT is passed
        // as a query param because a redirect cannot carry an auth header.
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
        // URLS, THIS BREAKS THE AUTH
        const token = localStorage.getItem(TOKEN_KEY) || "";
        window.location.href = `${API}/auth/google/start?token=${encodeURIComponent(
            token,
        )}`;
    };

    const handleDisconnect = async () => {
        setWorking(true);
        try {
            await api.post("/auth/google/disconnect");
            await refreshUser();
            toast.success("Google account disconnected.");
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Could not disconnect. Try again.",
            );
        } finally {
            setWorking(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="google-connect-dialog"
                onOpenAutoFocus={() => {
                    openerRef.current = document.activeElement;
                }}
                onCloseAutoFocus={(event) => {
                    // Radix only auto-restores focus to a DialogTrigger; this
                    // dialog is opened programmatically, so restore focus to
                    // the real opener here.
                    const el = openerRef.current;
                    if (el && typeof el.focus === "function") {
                        event.preventDefault();
                        try {
                            el.focus();
                        } catch (_e) {
                            /* noop */
                        }
                    }
                }}
                className="max-w-md border-white/10 bg-surface-raised p-0 overflow-hidden sm:rounded-lg"
            >
                <div className="relative p-6 sm:p-7">
                    <DialogHeader>
                        <DialogTitle className="rf-heading flex items-center gap-2 text-xl font-bold text-white">
                            <GoogleG className="h-5 w-5" />
                            Connect Google
                        </DialogTitle>
                        <DialogDescription className="text-white/55">
                            Authorize ReelFlo to access your Google Docs &amp; Drive.
                            This is optional — you can connect it anytime.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-5 space-y-4">
                        {connected ? (
                            <>
                                <div
                                    data-testid="google-status-connected"
                                    className="flex items-center gap-3 rounded-lg border border-emerald-400/25 bg-emerald-500/10 px-4 py-3"
                                >
                                    <CheckCircle2 className="h-5 w-5 flex-none text-emerald-300" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-emerald-200">
                                            Connected
                                        </p>
                                        {accountEmail ? (
                                            <p
                                                data-testid="google-account-email"
                                                className="truncate text-sm text-white/70"
                                            >
                                                {accountEmail}
                                            </p>
                                        ) : null}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    data-testid="google-disconnect-button"
                                    disabled={working}
                                    onClick={handleDisconnect}
                                    className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-rose-400/30 bg-rose-500/10 min-h-11 px-5 text-sm font-semibold text-rose-200 transition-colors hover:border-rose-300/50 hover:bg-rose-500/20 disabled:opacity-60"
                                >
                                    {working ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Link2Off className="h-4 w-4" />
                                    )}
                                    Disconnect Google
                                </button>
                            </>
                        ) : (
                            <>
                                <div
                                    data-testid="google-status-disconnected"
                                    className="rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white/60"
                                >
                                    Not connected
                                </div>

                                <button
                                    type="button"
                                    data-testid="google-connect-button"
                                    onClick={handleConnect}
                                    className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-white min-h-11 px-5 text-sm font-semibold text-[#1f1f1f] shadow-sm transition-transform duration-150 hover:scale-[1.01] active:scale-[0.99]"
                                >
                                    <GoogleG className="h-5 w-5" />
                                    Connect Google
                                </button>

                                <p className="flex items-center gap-2 text-xs text-white/40">
                                    <ShieldCheck className="h-3.5 w-3.5" />
                                    Secure Google authorization · revoke anytime
                                </p>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
